import React from "react";

import { css } from "@emotion/react";

import { CSS_COLORS } from "../../../../../styles/styles-css-variables";
import { StyledLoadingSpinner } from "../../../../../routes/modal-loading-spinner";
import { CSS_MQ } from "../../../../../styles/breakpoints";
import { CbObjOnBrand, CVCPolicyType } from "@adyen/adyen-web/dist/types/components/internal/SecuredFields/lib/types";
import cn from "classnames";
import { BrandObject } from "@adyen/adyen-web/dist/types/components/Card/types";
import { ReactTFC } from "../../../../../utils/fp/missing-ts-parts";
import SecuredFieldsElement from "@adyen/adyen-web/dist/types/components/SecuredFields";
import { jsHooks } from "@swo/shared";
import { FONTS, fontSize } from "../../../../../styles/fonts";
import { BrandsPicker } from "./BrandsPicker";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../../../../components/T";

export const AdyenIframeTemplate: ReactTFC<{
  elementId: string;
  instance: SecuredFieldsElement | null;
  selectedBrand: CbObjOnBrand | null;
  isReady: boolean;
  cvcPolicy: CVCPolicyType | undefined;
  supportedBrands: Array<BrandObject> | null;
  onBrandPicked: (brand: string) => void;
  translations: {
    creditCardNumber: string | TranslatedValue;
    expiryDate: string | TranslatedValue;
    securityCode: string | TranslatedValue;
  };
}> = props => {
  const allBrands = props.supportedBrands
    ? [...props.supportedBrands]
    : props.selectedBrand
      ? [props.selectedBrand]
      : null;

  return (
    <div
      id={props.elementId}
      css={css`
        display: flex;
        flex-direction: column;
        gap: 24px;
        margin-top: 24px;
        margin-bottom: 24px;
      `}
    >
      {!props.isReady && (
        <div>
          <StyledLoadingSpinner />
        </div>
      )}

      <CCFormField
        label={props.translations.creditCardNumber}
        dataCse={"encryptedCardNumber"}
        dataClassName={jsHooks.checkout.paymentMethods.creditCard.ccNumber}
        isNarrow={false}
      >
        {allBrands && (
          <div
            css={css`
              position: absolute;
              right: 0;
            `}
          >
            <BrandsPicker
              supportedBrands={allBrands}
              selectedBrand={props.selectedBrand}
              onBrandPicked={brand => {
                props.onBrandPicked(brand.brand);
              }}
            />
          </div>
        )}
      </CCFormField>

      <CCFormField
        label={props.translations.expiryDate}
        dataCse={"encryptedExpiryDate"}
        dataClassName={jsHooks.checkout.paymentMethods.creditCard.expiryDate}
        isNarrow={true}
      />

      <CCFormField
        className={cn({ hidden: props.cvcPolicy === "hidden" })}
        label={props.translations.securityCode}
        dataCse={"encryptedSecurityCode"}
        dataClassName={jsHooks.checkout.paymentMethods.creditCard.securityCode}
        isNarrow={true}
      />
    </div>
  );
};

const CCFormField: React.FC<{
  className?: string;
  label: string | TranslatedValue;
  dataCse: string;
  dataClassName: string;
  children?: React.ReactNode;
  isNarrow: boolean;
}> = props => {
  return (
    <label css={labelStyles} className={cn(props.className)}>
      <span css={spanStyles}>
        <T>{props.label}</T>:
      </span>
      <span
        data-cse={props.dataCse}
        css={iframeWrapperStyles}
        className={cn(props.dataClassName, props.isNarrow ? "narrower" : "")}
      />

      {props.children}
    </label>
  );
};

const iframeWrapperStyles = css`
  height: 40px;
  padding: 0 12px;

  iframe {
    height: 40px !important;
  }

  border: 1px solid ${CSS_COLORS.Green};
  border-radius: 8px;

  &.narrower {
    max-width: 8em;
  }
`;

const labelStyles = [
  FONTS.SwarovskiFutura300,
  css`
    position: relative;

    display: grid;
    align-items: center;

    ${CSS_MQ.atMostSm} {
      margin-top: 20px;
    }

    &.hidden {
      visibility: hidden;
      height: 0;
    }
  `,
];

const spanStyles = [
  fontSize.x2s,
  css`
    margin-right: 10px;

    position: absolute;
    top: -0.5em;
    left: 12px;

    background: ${CSS_COLORS.GreenTransparent20};
    border-radius: 8px;
  `,
];
